import { api, uploadForm } from '../lib/api.js';
import { FAILURE_CAUSE_LABEL } from './visits.js';
import type { FailureCause } from './visits.js';
import type { ServiceExpense } from './service.js';

export const SEVERITY_LABEL: Record<string, string> = {
  PRODUCTION_STOPPED: 'עוצר ייצור', MACHINE_DOWN: 'מכונה מושבתת', NO_URGENCY: 'ללא דחיפות',
};
export const SEVERITIES = ['PRODUCTION_STOPPED', 'MACHINE_DOWN', 'NO_URGENCY'] as const;

export const UPDATE_KINDS = ['RESPONSE', 'REPORT', 'NOTE'] as const;
export const UPDATE_KIND_LABEL: Record<string, string> = {
  RESPONSE: 'מענה ראשוני', REPORT: 'דוח ביצוע', NOTE: 'הערה פנימית',
};

export type CallUpdateRow = {
  id: string; kind: string; kindLabel: string; text: string; hoursSpent: number | null;
  visibleToCustomer: boolean; authorName: string | null; createdAt: string;
};

/** „תמונות ומסמכים" של קריאה — רשימה בלבד; כל פריט מוגש מהנתיב הקיים שלו (call-media.ts בשרת). */
export type CallMediaSource = 'CALL' | 'JOB' | 'GAP' | 'NC' | 'VISIT' | 'SIGNATURE' | 'REPORT' | 'RECEIPT' | 'DOCUMENT';
export type CallMediaItem = {
  key: string; source: CallMediaSource; kind: 'image' | 'file'; url: string; title: string; context: string | null;
  at: string | null; byName: string | null; customerVisible: boolean;
  remove: { kind: 'JOB_PHOTO' | 'DOCUMENT'; id: string } | null;
};
export type CallMediaView = {
  items: CallMediaItem[];
  uploadTargets: { jobs: Array<{ id: string; label: string }>; projectId: string | null; projectNumber: string | null };
};

export type ServiceCallRow = {
  id: string;
  /** 260068-S01, or S260001 — what the customer quotes. */
  callNumber: string | null; number: string | null; projectId: string | null; customerName: string; severity: string; severityLabel: string;
  description: string; status: string; statusLabel: string;
  contactName: string | null; contactPhone: string | null; contactEmail: string | null;
  openedByName: string | null; hasPhoto: boolean;
  /** לפרויקט יש תמונה (`projectImageUrl(number)`). */
  projectHasImage: boolean;
  openedAt: string; responseDeadline: string | null; resolutionDeadline: string | null;
  respondedAt: string | null; resolvedAt: string | null; closedAt: string | null; handledByName: string | null;
  /** המטפל המשויך (U-1009-05). */
  handledById: string | null;
  updates: CallUpdateRow[];
  itemRef: string | null; linkedSku: string | null; linkedPartId: string | null; linkedPartNumber: string | null;
  jobs: CallJobRow[];
  // Day-work request fields (requestType = DAY_WORK — U-1010-08: `kind` is gone).
  crewSize: number | null; hazards: string[]; hazardLabels: string[];
  neededFrom: string | null; neededTo: string | null;
  equipmentNo: string | null; roomNo: string | null; customerOrderNo: string | null;
  // What the customer stated + the declaration they accepted when opening.
  manufacturedByEdal: boolean | null;
  termsVersion: number | null; termsAcceptedAt: string | null; termsAcceptedName: string | null;
  // „קריאה חדשה" (U-1009-03): המערכת, סוג הבקשה, והכיסוי שהוצע בפתיחה.
  system: { id: string; name: string; origin: string; serialNumber: string | null } | null;
  requestType: string; requestTypeLabel: string;
  coverage: { kind: 'WARRANTY' | 'CONTRACT' | 'PAID' | 'UNKNOWN'; label: string; reason: string; until: string | null } | null;
  /** בתשלום, בלי PO ובלי תקנון חתום. */
  paymentPending: boolean;
  termsRequestedAt: string | null;
  /** פרויקט השירות של הקריאה (U-1009-04). null = קריאה ישנה. */
  serviceProject: { id: string; number: string } | null;
  priorityQuoteNo: string | null;
  /** קריאה שנפתחה לבד לאחזקה מתוכננת (U-1009-11). periods = SEMIANNUAL/ANNUAL/BIENNIAL. */
  maintenance: { periods: string[]; labels: string; dueOn: string } | null;
  /** איפה הקריאה בתהליך — 8 שלבים + הצעד הבא (U-1010-02). השרת מחשב; אופציונלי עד שהוא שולח. */
  process?: CallProcess;
  /** החלטת השטח בשלב „החלטה בשטח": תיקון במקום, או הצעת מחיר. */
  fieldDecision?: FieldDecision | null;
  /** U-1010-04: בקשת „תיקון במקום" מהשטח ממתינה למנהלת השירות. */
  repairRequestPending?: boolean;
  /** U-1010-03 „מחיר מוסכם" — כשמוגדר, הוא הסכום לחיוב בסגירה. */
  agreedPriceIls?: number | null;
  /** U-1010-03 — נסגרה בדיאלוג הסגירה. */
  closeBilling?: { at: string; paidIls: number; freeIls: number; invoiced: boolean; byName?: string | null } | null;
  /** יש עבודת שטח — „נפתרה"/„נסגרה" רק דרך „סגירה וחיוב" (השרת דוחה, CLOSE_VIA_BILLING). */
  closeViaBilling?: boolean;
  /** U-1010-05: ההצעה האחרונה שנשלחה ללקוח לאישור בפורטל. */
  repairQuote?: RepairQuoteRow | null;
  /** U-1010-07 (החלטה 122): שעון ה-SLA מושהה — למה ומאז; בכמה דקות זזו היעדים עד כה. */
  slaPause?: SlaPauseState | null;
  /** U-1010-07 (החלטה 120): „תקלה חוזרת" — מק"טים שנכשלו כאן פעם שנייה על אותה מערכת תוך 6 חודשים. */
  repeatFailureSkus?: string[];
};

export type WarrantyCause = 'MANUFACTURING' | 'DESIGN' | 'INSTALLATION' | 'SUPPLIER' | 'WEAR' | 'MISUSE' | 'UNCLASSIFIED';
export type WarrantyCall = {
  id: string; callNumber: string | null; closedOn: string; description: string;
  customerId: string; customerName: string; systemKey: string; systemName: string; manufacturer: string | null;
  cause: WarrantyCause; ils: number; supplierKeys: string[];
};
export type WarrantyCostReport = {
  summary: {
    totalIls: number; calls: number; avgIls: number;
    split: { hoursPct: number; partsPct: number; calloutsPct: number; expensesPct: number };
    supplierIls: number; supplierCalls: number;
    waivedMinimum: { ils: number; days: number };
    /** Old lines whose cost was reconstructed (not fixed at the close). */
    estimatedLines: number;
  };
  byCause: { cause: WarrantyCause; label: string; ils: number; calls: number }[];
  bySystem: { key: string; name: string; customerName: string; calls: number; ils: number; repeat: boolean; mix: { cause: WarrantyCause; ils: number }[] }[];
  bySupplier: { key: string; supplier: string; sku: string | null; name: string; times: number; ils: number; fleetRepeat: boolean }[];
  byMonth: { month: string; total: number; byCause: Partial<Record<WarrantyCause, number>> }[];
  calls: WarrantyCall[];
  options: { customers: { id: string; name: string }[]; manufacturers: string[] };
};

export type SlaPauseReason ='QUOTE' | 'CUSTOMER_DATE' | 'NO_ACCESS' | 'PARTS';
export type SlaPauseState = {
  current: { reason: SlaPauseReason; label: string; note: string | null; since: string; manual: boolean } | null;
  openReasons: SlaPauseReason[];
  extendedMinutes: number;
};

// ── U-1010-05 — הצעת מחיר לתיקון, לאישור הלקוח בפורטל ──
export type RepairQuoteRow = {
  id: string; quoteNo: string | null; totalIls: number; validUntil: string; note: string | null; pdfName: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  requestedAt: string; decidedAt: string | null;
  approverName: string | null; customerPoNo: string | null; rejectReason: string | null;
};

// ── סגירה עם חיוב (U-1010-03) ─────────────────────────────────────────────
export type ChargeCoverage = 'PAID' | 'FREE';
/** סיבת כשל מובנית (החלטה 16) — אותם ערכים בשטח, במשרד ובשרת. */
export type PartCause = FailureCause;
export const CAUSE_LABEL: Record<PartCause, string> = FAILURE_CAUSE_LABEL;
export type ChargeLine = {
  /** EXPENSE (U-1010-04): קבלן משנה / השכרה / רכש בשטח — עלות + תוספת החלקים. */
  key: string; kind: 'HOURS' | 'CALLOUT' | 'PART' | 'ADJUST' | 'EXPENSE'; label: string; detail: string;
  date: string | null; employeeId: string | null; employeeName: string | null; sku: string | null;
  qty: number; unitIls: number; totalIls: number;
  proposed: ChargeCoverage; proposedWhy: string; coverage: ChargeCoverage; reason: string | null;
  cause: PartCause | null; causeAuto: PartCause | null;
  fieldDayId: string | null; partEventId: string | null; visitItemId: string | null;
  expenseId?: string | null;
};
export type RateCard = {
  manufacturer: string | null; source: 'CARD' | 'DEFAULT';
  callOutIls: number; hourlyIls: number; partsMarkupPct: number; minPerTechDayIls: number | null;
};
export type CloseBillingPreview = {
  callId: string; callNumber: string | null; status: string;
  requestType: string; requestTypeLabel: string;
  coverageKind: string; coverageLabel: string; contractTerms: string | null;
  rate: RateCard; lines: ChargeLine[];
  linesPaidIls: number; freeIls: number; agreedPriceIls: number | null; paidTotalIls: number; techDays: number;
  warnings: string[];
  closed: { at: string; paidIls: number; freeIls: number; invoiced: boolean; milestoneId: string | null } | null;
  blocked: string | null;
};
export type CloseBillingInput = {
  finding?: 'MISUSE' | 'NO_FAULT' | null;
  causes?: Record<string, PartCause>;
  lines?: { key: string; coverage: ChargeCoverage; reason?: string | null }[];
};
export type RateCardRow = { id: string; manufacturer: string; callOutIls: number; hourlyIls: number; partsMarkupPct: number; minPerTechDayIls: number | null };

export type ProcessStepKey = 'OPEN' | 'TRIAGE' | 'DIAG' | 'DECIDE' | 'QUOTE' | 'WORK' | 'REPORT' | 'CLOSE';
export type ProcessStepState = 'done' | 'now' | 'todo' | 'skip';
export type ProcessWho = 'CUSTOMER' | 'SERVICE_MGR' | 'FIELD' | 'OFFICE';
export type FieldDecision = 'REPAIR_NOW' | 'QUOTE';
export type CallProcess = {
  stage: number;
  steps: Array<{ key: ProcessStepKey; label: string; state: ProcessStepState }>;
  next: { who: ProcessWho | null; whoLabel: string; text: string };
};

export type ServiceRoute = {
  requiresEngineering: boolean; requiresProduction: boolean; requiresPurchasing: boolean;
  requiresShipping: boolean; requiresInstallation: boolean;
};
export type ServiceProjectStrip = {
  id: string; number: string;
  parent: { id: string; number: string } | null;
  pm: { id: string; name: string } | null;
  managers: { id: string; name: string; role: string }[];
  route: ServiceRoute;
  fieldOnly: boolean;
  costOn: 'ORDER' | 'SERVICE';
  money: { cost: number; billed: number; invoiced: number } | null;
};
export type ClosingKind = 'inv' | 'po' | 'sla' | 'rep' | 'claim' | 'sign';
export type ClosingRow = {
  key: string; kind: ClosingKind; kindLabel: string;
  callId: string; callNumber: string | null; customerName: string; systemName: string | null;
  requestType: string; requestTypeLabel: string;
  what: string; detail: string; since: string;
  action: 'billing-row' | 'invoices' | 'po' | 'open' | 'visit-sign';
  customerOrderNo: string | null; priorityQuoteNo: string | null; termsRequestedAt: string | null;
  suggestedIls: number | null;
  /** שורת sign — ביקור אחזקה שממתין לחתימה מרחוק (U-1009-08 part 2). */
  visitId: string | null; contactEmail: string | null;
  signatureRequestedAt: string | null; signatureRequestedTo: string | null;
};

export const REQUEST_TYPES = ['FAULT', 'MAINTENANCE', 'DAY_WORK', 'BUILD'] as const;
export const REQUEST_TYPE_LABEL: Record<string, string> = {
  FAULT: 'תקלה', MAINTENANCE: 'אחזקה', DAY_WORK: 'עובדים לפי יומית / שעות', BUILD: 'ייצור / שיפוץ',
};

export type NewCallBody = {
  systemId?: string;
  newSystem?: {
    origin: 'NOT_OURS' | 'EDAL_PROJECT'; customerId: string; name: string;
    manufacturer?: string | null; serialNumber?: string | null; siteName?: string | null;
    projectNumber?: string | null; deliveredAt?: string | null;
  };
  dayWorkCustomerId?: string;
  requestType: string; severity: string; description: string;
  contactName?: string; contactPhone?: string; contactEmail?: string; customerOrderNo?: string;
  crewSize?: number; neededFrom?: string; neededTo?: string; hazards?: string[];
  sendTerms?: boolean;
  /** „פתח שירות" (אלי 12/10/2026): מחיר שסוכם — גובר בסגירה. */
  agreedPriceIls?: number | null;
  /** סיור 12/10 A9: מספר ההצעה בפריוריטי (PQ…). */
  priorityQuoteNo?: string | null;
};

/** close-together.ts בשרת. */
export type CloseTogether = {
  jobs: { id: string; label: string; status: string; date: string | null; crew: string[] }[];
  customerMail: { to: string | null; willQueue: boolean; why: string | null };
};

export type ServiceTermsRow = { id: string; version: number; title: string; body: string; isActive: boolean; createdAt: string; createdByName: string | null };

// Field work raised from the call — crew + the hours they actually logged.
export type CallJobRow = {
  id: string; type: string; status: string; startDate: string | null; endDate: string | null;
  crew: string[]; actualHours: number;
};

// Traceability board — the milestones of every call's life.
export type BoardCall = {
  id: string; requestType: string; customerName: string; number: string | null;
  severity: string; severityLabel: string; status: string; statusLabel: string;
  description: string; handledByName: string | null;
  openedAt: string; respondedAt: string | null; partsOrderedAt: string | null; partsOrderedCount: number;
  resolvedAt: string | null; closedAt: string | null;
  responseDeadline: string | null; resolutionDeadline: string | null;
  responseMinutes: number | null; resolutionMinutes: number | null;
};
export type BoardStats = {
  open: number; overdueResponse: number; overdueResolution: number;
  avgResponseMinutes: number | null; avgResolutionMinutes: number | null;
  metResponse: number; respondedTotal: number;
};
export type ServiceBoardData = { columns: { key: string; label: string; calls: BoardCall[] }[]; stats: BoardStats };

export type CallVisit = {
  id: string;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'AWAITING_SIGNATURE' | 'CLOSED' | 'CANCELLED';
  periodLabel: string; date: string | null; technicianName: string | null;
  answered: number; total: number;
  signatureRequestedAt: string | null; signatureRequestedTo: string | null; signatureRemindedAt: string | null;
  signedAt: string | null; signerName: string | null;
  contactEmail: string | null;
  /** אלי 12/10/2026: „החלף טכנאי / יום" ושורת „הבא". */
  technicianId: string | null; scheduledFor: string | null; codeActive: boolean;
  /** עבודת השטח של הביקור — „החלף טכנאי / יום" פותח עליה את „מתי ומי" (סיור 12/10 A2). */
  jobId?: string | null;
  /** החלטה 144: פעולות עם „צלם — חובה" שנענו בלי צילום. */
  photoMissing?: string[];
  /** החלטה 148: „דורש חלק" מהשטח — ומאיזו טיוטת הצעה. */
  partsNeeded?: Array<{ action: string; sku: string; desc: string; qty: number; quoteId: string | null }>;
};

/** תשובת פתיחת קריאה. `termsQueued` — מייל התקנון נכנס לתור „ממתין לאישור שליחה". */
export type NewCallResult = {
  id: string; callNumber: string | null; termsSent: boolean;
  termsQueued?: boolean; termsError?: string | null;
};

export const serviceCallsApi = {
  board: () => api<ServiceBoardData>('/api/service-calls/board'),
  list: (status?: string) => api<ServiceCallRow[]>(`/api/service-calls${status ? `?status=${status}` : ''}`),
  /** קריאה אחת באותה צורה של הרשימה — לתצוגה המקדימה בחלון החיפוש. */
  one: async (id: string): Promise<ServiceCallRow | null> =>
    (await api<ServiceCallRow[]>(`/api/service-calls?status=all&id=${encodeURIComponent(id)}`))[0] ?? null,
  open: (body: NewCallBody) => api<NewCallResult>('/api/service-calls', { method: 'POST', body }),
  /** `queued` — המייל נכנס לתור האישור ולא יצא עדיין. */
  requestTerms: (id: string) => api<{ ok: true; to: string; queued: boolean }>(`/api/service-calls/${id}/request-terms`, { method: 'POST' }),
  addUpdate: (id: string, body: { kind: string; text: string; hoursSpent?: number | null; visibleToCustomer?: boolean }) =>
    api<CallUpdateRow>(`/api/service-calls/${id}/updates`, { method: 'POST', body }),
  deleteUpdate: (updateId: string) => api<{ ok: true }>(`/api/service-calls/updates/${updateId}`, { method: 'DELETE' }),
  setSeverity: (id: string, severity: string) => api<{ ok: true }>(`/api/service-calls/${id}/severity`, { method: 'POST', body: { severity } }),
  // U-1009-05: מטפל משויך + יעדי SLA בקריאה (עם סיבה).
  handlerOptions: () => api<{ id: string; name: string; role?: string }[]>('/api/service-calls/handler-options'),
  setHandler: (id: string, userId: string | null) => api<{ ok: true }>(`/api/service-calls/${id}/handler`, { method: 'POST', body: { userId } }),
  setSla: (id: string, body: { responseDeadline?: string | null; resolutionDeadline?: string | null; reason: string }) =>
    api<{ ok: true }>(`/api/service-calls/${id}/sla`, { method: 'POST', body }),
  /** „ממתין ללקוח" / „חלקים בהזמנה" — השהיית השעון (U-1010-07). */
  slaPause: (id: string, reason: Exclude<SlaPauseReason, 'QUOTE'>, note: string | null) =>
    api<{ ok: true }>(`/api/service-calls/${id}/sla-pause`, { method: 'POST', body: { reason, note } }),
  /** „עלות אחריות" בדשבורד השירות (U-1010-07, החלטה 123). */
  warrantyCost: (q: { from?: string; to?: string; customerId?: string; manufacturer?: string; cause?: string }) => {
    const p = new URLSearchParams(Object.entries(q).filter(([, v]) => !!v) as [string, string][]).toString();
    return api<WarrantyCostReport>(`/api/service-calls/warranty-cost${p ? `?${p}` : ''}`);
  },
  slaResume: (id: string) =>
    api<{ ok: true; extendedMinutes: number }>(`/api/service-calls/${id}/sla-resume`, { method: 'POST', body: {} }),
  terms: () => api<ServiceTermsRow[]>('/api/service-calls/terms'),
  publishTerms: (title: string, body: string) => api<{ id: string; version: number }>('/api/service-calls/terms', { method: 'POST', body: { title, body } }),
  // U-1009-04: פרויקט השירות, „לסגירה". („שייך לפרויקט" הוסר — החלטה 64.)
  project: (id: string) => api<ServiceProjectStrip | null>(`/api/service-calls/${id}/project`),
  /** ביקורי האחזקה של הקריאה — מצב, דוח, חתימה (U-1009-08). */
  visits: (id: string) => api<CallVisit[]>(`/api/service-calls/${id}/visits`),
  setRoute: (id: string, route: Partial<ServiceRoute>) => api<{ ok: true }>(`/api/service-calls/${id}/route`, { method: 'POST', body: route }),
  setPm: (id: string, userId: string | null) => api<{ ok: true }>(`/api/service-calls/${id}/pm`, { method: 'POST', body: { userId } }),
  closing: () => api<ClosingRow[]>('/api/service-calls/closing'),
  closingCount: () => api<{ count: number }>('/api/service-calls/closing/count'),
  setBillingRefs: (id: string, refs: { customerOrderNo?: string | null; priorityQuoteNo?: string | null }) =>
    api<{ ok: true }>(`/api/service-calls/${id}/billing-refs`, { method: 'POST', body: refs }),
  remind: (id: string, text: string) => api<{ ok: true }>(`/api/service-calls/${id}/remind`, { method: 'POST', body: { text } }),
  billingRow: (id: string, amount: number) => api<{ id: string }>(`/api/service-calls/${id}/billing-row`, { method: 'POST', body: { amount } }),
  // closingGaps: what is still open at the customer. Optional, and when it is
  // filled the office gets an internal notice — the customer never sees it.
  setStatus: (id: string, status: string, closingGaps?: string) =>
    api<{ ok: true }>(`/api/service-calls/${id}/status`, { method: 'POST', body: { status, closingGaps } }),
  /** סיור 12/10 (החלטה 166, A15): מה נסגר יחד עם הקריאה ולאן ייצא דוח הסגירה. */
  closeTogether: (id: string) => api<CloseTogether>(`/api/service-calls/${id}/close-together`),
  setContactEmail: (id: string, email: string) =>
    api<{ ok: true; email: string }>(`/api/service-calls/${id}/contact-email`, { method: 'POST', body: { email } }),
  /** עריכת פרטי הפתיחה (אלי 29/10/2026) — כל שינוי נרשם כהערה פנימית. */
  updateDetails: (id: string, body: { description?: string; contactName?: string | null; contactPhone?: string | null; contactEmail?: string | null; requestType?: string }) =>
    api<{ ok: true; changed: number }>(`/api/service-calls/${id}/details`, { method: 'POST', body }),
  photoUrl: (id: string) => `/api/service-calls/${id}/photo`,
  media: (id: string) => api<CallMediaView>(`/api/service-calls/${encodeURIComponent(id)}/media`),
  /** U-1010-02 — „תיקון במקום" / „צריך הצעת מחיר"; null מבטל את ההחלטה. */
  setFieldDecision: (id: string, decision: FieldDecision | null) =>
    api<{ ok: true }>(`/api/service-calls/${id}/field-decision`, { method: 'POST', body: { decision } }),
  // U-1010-03 — סגירה עם חיוב, מחיר מוסכם, מחירון ליצרן.
  closePreview: (id: string, body: CloseBillingInput) =>
    api<CloseBillingPreview>(`/api/service-calls/${id}/close-billing/preview`, { method: 'POST', body }),
  closeBilling: (id: string, body: CloseBillingInput) =>
    api<{ milestoneId: string | null; paidTotalIls: number; freeIls: number; lines: number }>(`/api/service-calls/${id}/close-billing`, { method: 'POST', body }),
  setAgreedPrice: (id: string, amountIls: number | null) =>
    api<{ ok: true }>(`/api/service-calls/${id}/agreed-price`, { method: 'POST', body: { amountIls } }),
  rateCards: () => api<{ defaults: RateCard; cards: RateCardRow[] }>('/api/service-calls/rate-cards'),
  saveRateCard: (body: Omit<RateCardRow, 'id'> & { id?: string | null }) =>
    api<RateCardRow>('/api/service-calls/rate-cards', { method: 'POST', body }),
  deleteRateCard: (id: string) => api<{ ok: true }>(`/api/service-calls/rate-cards/${id}`, { method: 'DELETE' }),
  // U-1010-04 — הטכנאי בתוך הקריאה: בקשות „תיקון במקום", ממצאים, הוצאות.
  tech: (id: string) => api<CallTech>(`/api/service-calls/${encodeURIComponent(id)}/tech`),
  decideRepair: (id: string, requestId: string, approve: boolean, note: string | null) =>
    api<RepairRequestRow[]>(`/api/service-calls/${encodeURIComponent(id)}/repair-requests/${encodeURIComponent(requestId)}/decide`, { method: 'POST', body: { approve, note } }),
  // U-1010-05 — הצעה לתיקון: PDF מפריוריטי + סה״כ + תוקף → מייל (תור האישור) → הלקוח בפורטל.
  sendQuote: (id: string, body: { file: File; totalIls: number; validUntil: string; note?: string; toEmail?: string }) => {
    const fd = new FormData();
    fd.append('totalIls', String(body.totalIls));
    fd.append('validUntil', body.validUntil);
    if (body.note) fd.append('note', body.note);
    if (body.toEmail) fd.append('toEmail', body.toEmail);
    fd.append('file', body.file);
    return uploadForm<{ quote: RepairQuoteRow; queued: boolean; to: string }>(`/api/service-calls/${encodeURIComponent(id)}/quotes`, fd, 'POST', { fallbackMessage: 'ההצעה לא נשלחה — נסו שוב' });
  },
  cancelQuote: (id: string, quoteId: string) =>
    api<{ ok: true }>(`/api/service-calls/${encodeURIComponent(id)}/quotes/${encodeURIComponent(quoteId)}/cancel`, { method: 'POST' }),
  quotePdfUrl: (id: string, quoteId: string): string => `/api/service-calls/${encodeURIComponent(id)}/quotes/${encodeURIComponent(quoteId)}/pdf`,
  openFindingNc: (id: string, gapId: string, partId: string | null) =>
    api<{ ncId: string; ncLink: string }>(`/api/service-calls/${encodeURIComponent(id)}/findings/${encodeURIComponent(gapId)}/nc`, { method: 'POST', body: { partId } }),
};

// ── U-1010-04 ──
export type RepairRequestRow = {
  id: string; jobId: string; employeeName: string; finding: string;
  hours: number | null; partsText: string | null; photoUrl: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string; decidedAt: string | null; decidedByName: string | null; answerNote: string | null;
};
export type CallFinding = {
  id: string; typeLabel: string; description: string; partNumber: string | null; photoUrls: string[];
  openedByName: string; createdAt: string; status: string; statusLabel: string; sideLabel: string;
  answer: string | null; ncId: string | null; ncLink: string | null; canOpenNc: boolean;
  failureCause: FailureCause | null; failureCauseLabel: string | null;
};
export type CallTech = {
  repairRequests: RepairRequestRow[];
  findings: CallFinding[];
  parts: { id: string; partNumber: string; name: string | null }[];
  linkedPartId: string | null;
  expenses: ServiceExpense[];
};
